import { create } from 'zustand';

import envConfig from '@/env.config';
import { LoginModalPage } from '@/lib/enums';

interface BreadcrumbItem {
  label: string;
  link: string;
  isCurrentPage: boolean;
}

interface AppStoreState {
  // NavBar
  hasScrolled: boolean;
  setHasScrolled: (hasScrolled: boolean) => void;

  // Breadcrumb
  breadcrumbItemsDesktop: BreadcrumbItem[];
  setBreadcrumbItemsDesktop: (breadcrumbItems: BreadcrumbItem[]) => void;
  breadcrumbItemMobile: BreadcrumbItem;
  setBreadcrumbItemMobile: (breadcrumbItem: BreadcrumbItem) => void;

  // Login Modal
  showLoginModal: boolean;
  setShowLoginModal: (showLoginModal: boolean) => void;
  loginModalPageToDisplay: LoginModalPage;
  setLoginModalPageToDisplay: (loginModalPageToDisplay: LoginModalPage) => void;
  disableOtpRequests: boolean;
  setDisableOtpRequests: (disableOtpRequests: boolean) => void;

  // Redirect Modal (Internal)
  showInternalRedirectModal: boolean;
  setShowInternalRedirectModal: (showInternalRedirectModal: boolean) => void;
  internalLinkToOpenInRedirectModal: string;
  setInternalLinkToOpenInRedirectModal: (url: string) => void;

  // Redirect Modal (External)
  showExternalRedirectModal: boolean;
  setShowExternalRedirectModal: (showExternalRedirectModal: boolean) => void;
  externalLinkToOpenInRedirectModal: string;
  setExternalLinkToOpenInRedirectModal: (url: string) => void;

  // Mobile Drawer
  showMobileDrawer: boolean;
  setShowMobileDrawer: (showMobileDrawer: boolean) => void;

  // Header Navbar has shadow
  hasShadow: boolean;
  setHasShadow: (hasShadow: boolean) => void;

  // CustomNavigationBar has shadow
  customNavigationBarHasShadow: boolean;
  setCustomNavigationBarHasShadow: (hasShadow: boolean) => void;

  // Filter section is sticky
  filterSectionSticky: boolean;
  setFilterSectionSticky: (filterSectionSticky: boolean) => void;

  //////////////////////////////
  // Feature Flags
  //////////////////////////////

  // UAT Login
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;

  // New Design
  isNewDesign: boolean;
  setIsNewDesign: (isNewDesign: boolean) => void;

  // Collection
  isCollectionEnabled: boolean;
  setIsCollectionEnabled: (isCollectionEnabled: boolean) => void;
}

export const useAppStore = create<AppStoreState>()(set => ({
  // NavBar
  hasScrolled: false,
  setHasScrolled: hasScrolled => set({ hasScrolled }),

  // Breadcrumb
  breadcrumbItemsDesktop: [
    {
      label: '',
      link: '',
      isCurrentPage: false,
    },
  ],
  setBreadcrumbItemsDesktop: breadcrumbItemsDesktop =>
    set({ breadcrumbItemsDesktop }),
  breadcrumbItemMobile: {
    label: '',
    link: '',
    isCurrentPage: false,
  },
  setBreadcrumbItemMobile: breadcrumbItemMobile =>
    set({ breadcrumbItemMobile }),

  // Login Modal
  showLoginModal: false,
  setShowLoginModal: showLoginModal => set({ showLoginModal }),
  loginModalPageToDisplay: LoginModalPage.EmailInput,
  setLoginModalPageToDisplay: loginModalPageToDisplay =>
    set({
      loginModalPageToDisplay,
    }),
  disableOtpRequests: false,
  setDisableOtpRequests: disableOtpRequests => set({ disableOtpRequests }),

  // Redirect Modal (Internal)
  showInternalRedirectModal: false,
  setShowInternalRedirectModal: showInternalRedirectModal =>
    set({ showInternalRedirectModal }),
  internalLinkToOpenInRedirectModal: '',
  setInternalLinkToOpenInRedirectModal: internalLinkToOpenInRedirectModal =>
    set({
      internalLinkToOpenInRedirectModal,
    }),

  // Redirect Modal (External)
  showExternalRedirectModal: false,
  setShowExternalRedirectModal: showExternalRedirectModal =>
    set({ showExternalRedirectModal }),
  externalLinkToOpenInRedirectModal: '',
  setExternalLinkToOpenInRedirectModal: externalLinkToOpenInRedirectModal =>
    set({
      externalLinkToOpenInRedirectModal,
    }),

  // Mobile Drawer
  showMobileDrawer: false,
  setShowMobileDrawer: showMobileDrawer => set({ showMobileDrawer }),

  // Header Navbar has shadow
  hasShadow: false,
  setHasShadow: hasShadow => set({ hasShadow }),

  // CustomNavigationBar has shadow
  customNavigationBarHasShadow: false,
  setCustomNavigationBarHasShadow: customNavigationBarHasShadow =>
    set({ customNavigationBarHasShadow }),

  // Filter section is sticky
  filterSectionSticky: false,
  setFilterSectionSticky: filterSectionSticky => set({ filterSectionSticky }),

  //////////////////////////////
  // Feature Flags
  //////////////////////////////

  // UAT Login
  isAuthenticated: !envConfig.internal.passwordProtect,
  setIsAuthenticated: isAuthenticated => set({ isAuthenticated }),

  // New Design
  isNewDesign: false,
  setIsNewDesign: isNewDesign => set({ isNewDesign }),

  // Collection
  isCollectionEnabled: false,
  setIsCollectionEnabled: isCollectionEnabled => set({ isCollectionEnabled }),
}));
